import { useEffect, useRef, useState } from "react";

import { useAPI } from "./useAPI";

interface MicroReviewResponse {
  success: boolean;
  message: string;
  review_id?: number;
}

type ReviewStatus = "loading" | "success" | "error";

interface UseMicroReviewSubmissionResult {
  status: ReviewStatus;
  errorMessage: string;
}

export function useMicroReviewSubmission(
  initialToken: string | null,
  onSuccess?: VoidFunction,
): UseMicroReviewSubmissionResult {
  const [status, setStatus] = useState<ReviewStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { unauthenticatedApi } = useAPI();
  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    const submitReview = async () => {
      // Prevent double submission
      if (hasSubmittedRef.current) {
        return;
      }

      if (!initialToken) {
        setStatus("error");
        setErrorMessage(
          "Link is broken. Please check the links sent to your email or contact support.",
        );
        return;
      }

      hasSubmittedRef.current = true;

      try {
        const response = await unauthenticatedApi<MicroReviewResponse>(
          "get",
          `/submit-shipment-micro-review?token=${encodeURIComponent(initialToken)}`,
        );

        if (response.data.success) {
          setStatus("success");
          onSuccess?.();
        } else {
          setStatus("error");
          setErrorMessage(
            response.data.message ||
              "Failed to submit review. Please try again or contact support.",
          );
        }
      } catch (error: any) {
        setStatus("error");
        const apiErrorMessage =
          error?.response?.data?.message ?? error?.response?.data?.detail;
        setErrorMessage(
          apiErrorMessage ??
            "Failed to submit review. Please try again or contact support.",
        );
      }
    };

    submitReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  return {
    status,
    errorMessage,
  };
}
